import React, { lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Field } from 'formik';
import { COOKIE_AUTH_DETAILS } from 'globalconfig/constants/keys';
import { getCookie } from 'globalservices/cookie';
import { FormikDatePicker } from 'globalcomponents/UI/Form/Base';
import { UI_DATE_LOWER_FORMAT } from 'globalconfig/constants/static';
import { getDateLocale, getFilterLocaleValues, phoneNumberValidation } from 'globalutils/fmUtils';
import AutoPayment from '../AutoPayment';
import { checkServiceProvider } from './serviceProviderConfig';
import { hasAnyValueMatch } from 'globalutils/underscoreUtil';

const layout = window.$environment?.CURRENT_LAYOUT;
const Input = lazy(() => import(`globalcomponents/UI/Input/${layout}`));
const Label = lazy(() => import(`globalcomponents/UI/FormLabel/${layout}`));
const SubHeader = lazy(() => import(`globalcomponents/SubHeader/${layout}`));
const Select = lazy(() => import(`globalcomponents/UI/Select/${layout}`));
const FormStyle = require(`globalcomponents/UI/Form/${layout}/serverStyle`).default;

export default function LeftForm({
  Styled,
  values,
  handleEdit,
  showError,
  errors,
  isNotValid,
  hasSaveApiStatus,
  dotUpdation,
  type,
  states,
  allVehicleTypes,
  allCycleRules,
  allCycleExceptions,
}) {
  const i18n = useTranslation();
  const token = getCookie(COOKIE_AUTH_DETAILS);

  return (
    <Styled.leftSection>
      {type === 'edit' && checkServiceProvider('autoPayment', window.$environment.SERVICE_PROVIDER, false) && (
        <div>
          <SubHeader text={i18n.t('companyData.billingInformation')} className="subheaderSection companyTitle" variant="h2" />
          <AutoPayment {...{ values, handleEdit, showError, errors, isNotValid, hasSaveApiStatus }} />
        </div>
      )}
      {type === 'edit' && token?.roles?.includes('FLMANAGER') && token?.enterpriseFeatureDto?.cycleRuleEnabled && (
        <div className="cycleruleSection">
          <SubHeader text={i18n.t('common.cycleRule')} className="subheaderSection companyTitle" variant="h2" />
          <FormStyle.FieldsWrapper>
            <Label htmlFor="assignedCycleRules" variant="body1">
              {i18n.t('common.cycleRule')}
            </Label>
            <FormStyle.TextWrapper>
              <Field
                name="assignedCycleRules"
                id="assignedCycleRules"
                onChange={(event) => {
                  if (
                    !event ||
                    !event.length ||
                    (event.length && !hasAnyValueMatch(event, ['USA 60h/7 days', 'USA 70h/8 days', 'Texas 70h/7 day']))
                  ) {
                    handleEdit('', {
                      field: 'assignedCycleExceptions',
                    });
                    handleEdit(allVehicleTypes[0], {
                      field: 'assignedVehicleType',
                    });
                  }
                  if (
                    event &&
                    event.length &&
                    hasAnyValueMatch(event, ['USA 60h/7 days', 'USA 70h/8 days']) &&
                    hasAnyValueMatch(event, [
                      'Local Radius',
                      'Texas 70h/7 day',
                      'Canada 70h/7 day South',
                      'Canada 120h/14 day South',
                      'Canada 70h/7 day North',
                      'Canada 120h/14 day North',
                      'Mexico',
                    ])
                  ) {
                    handleEdit(allVehicleTypes[0], {
                      field: 'assignedVehicleType',
                    });
                  }
                  if (!event || !event.length) {
                    handleEdit(allVehicleTypes[0], {
                      field: 'assignedVehicleType',
                    });
                  }
                  handleEdit(event, { field: 'assignedCycleRules' });
                  handleEdit('', { field: 'defaultCycleRule' });
                }}
                as={Select}
                placeholder={i18n.t('common.pleaseSelect')}
                value={getFilterLocaleValues(values['assignedCycleRules'], i18n)}
                suggestions={getFilterLocaleValues(allCycleRules, i18n)}
                isClearable
                isMulti
              />
            </FormStyle.TextWrapper>
          </FormStyle.FieldsWrapper>

          <FormStyle.FieldsWrapper>
            <Label htmlFor="assignedCycleExceptions" variant="body1">
              {i18n.t('companyData.exceptions')}
            </Label>
            <FormStyle.TextWrapper>
              <Field
                name="assignedCycleExceptions"
                id="assignedCycleExceptions"
                onChange={(event) => {
                  handleEdit(event, {
                    field: 'assignedCycleExceptions',
                  });
                }}
                as={Select}
                placeholder={i18n.t('common.pleaseSelect')}
                value={getFilterLocaleValues(values['assignedCycleExceptions'], i18n)}
                suggestions={getFilterLocaleValues(allCycleExceptions, i18n)}
                isDisabled={
                  !values?.assignedCycleRules ||
                  !values?.assignedCycleRules.length ||
                  (values?.assignedCycleRules.length &&
                    !hasAnyValueMatch(values?.assignedCycleRules, ['USA 60h/7 days', 'USA 70h/8 days', 'Texas 70h/7 day']))
                }
                isClearable
                isMulti
              />
            </FormStyle.TextWrapper>
          </FormStyle.FieldsWrapper>

          <FormStyle.FieldsWrapper>
            <Label htmlFor="defaultCycleRule" variant="body1">
              {i18n.t('companyData.defaultCycleRule')}
            </Label>
            <FormStyle.TextWrapper>
              <Field
                name="defaultCycleRule"
                id="defaultCycleRule"
                onChange={(event) => handleEdit(event, { field: 'defaultCycleRule' })}
                as={Select}
                placeholder={i18n.t('common.pleaseSelect')}
                value={getFilterLocaleValues([values['defaultCycleRule']], i18n)}
                suggestions={getFilterLocaleValues(values['assignedCycleRules'] || [], i18n)}
              />
            </FormStyle.TextWrapper>
          </FormStyle.FieldsWrapper>

          <FormStyle.FieldsWrapper>
            <Label htmlFor="assignedVehicleType" variant="body1">
              {i18n.t('companyData.vehicleType')}
            </Label>
            <FormStyle.TextWrapper>
              <Field
                name="assignedVehicleType"
                id="assignedVehicleType"
                onChange={(event) => handleEdit(event, { field: 'assignedVehicleType' })}
                as={Select}
                placeholder={i18n.t('common.pleaseSelect')}
                value={getFilterLocaleValues([values['assignedVehicleType']], i18n)}
                suggestions={getFilterLocaleValues(
                  values?.assignedCycleRules &&
                    values?.assignedCycleRules.length &&
                    hasAnyValueMatch(values?.assignedCycleRules, ['USA 60h/7 days', 'USA 70h/8 days']) &&
                    !hasAnyValueMatch(values?.assignedCycleRules, [
                      'Local Radius',
                      'Texas 70h/7 day',
                      'Canada 70h/7 day South',
                      'Canada 120h/14 day South',
                      'Canada 70h/7 day North',
                      'Canada 120h/14 day North',
                      'Mexico',
                    ])
                    ? allVehicleTypes
                    : [{ label: ' Property', value: 'Property' }],
                  i18n
                )}
              />
            </FormStyle.TextWrapper>
          </FormStyle.FieldsWrapper>
        </div>
      )}
      {type === 'add' && (
        <>
          <div className="cycleruleSection">
            <SubHeader text={i18n.t('common.mainInformation')} className="subheaderSection companyTitle" variant="h2" />
            <FormStyle.FieldsWrapper>
              <Label htmlFor="company" variant="body1">
                {i18n.t('companies.companyName')}
              </Label>
              <FormStyle.TextWrapper>
                <Field
                  name="company"
                  handleOnChange={(event) => handleEdit(event, { field: 'company' })}
                  as={Input}
                  placeholder={i18n.t('companies.companyName')}
                  value={values['company']}
                  type="text"
                  className="borderStyle"
                  id="company"
                  onLoadFocus
                />
                {showError('company', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
              </FormStyle.TextWrapper>
            </FormStyle.FieldsWrapper>
            <FormStyle.FieldsWrapper>
              <Label htmlFor="dotNumber" variant="body1">
                {i18n.t('common.quoteId')}
              </Label>
              <FormStyle.TextWrapper>
                <Field
                  name="dotNumber"
                  id="dotNumber"
                  handleOnChange={(event) => handleEdit(event, { field: 'dotNumber' })}
                  as={Input}
                  placeholder={i18n.t('common.quoteId')}
                  value={values['dotNumber']}
                  type="text"
                  updateValue={(e) => dotUpdation(e)}
                />
                {showError('dotNumber', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
              </FormStyle.TextWrapper>
            </FormStyle.FieldsWrapper>
            <FormStyle.FieldsWrapper>
              <Label htmlFor="address" variant="body1">
                {i18n.t('common.address')}
              </Label>
              <FormStyle.TextWrapper>
                <Field
                  name="address"
                  id="address"
                  handleOnChange={(event) => handleEdit(event, { field: 'address' })}
                  as={Input}
                  placeholder={i18n.t('common.address')}
                  value={values['address']}
                  type="text"
                />
                {showError('address', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
              </FormStyle.TextWrapper>
            </FormStyle.FieldsWrapper>
            <FormStyle.FieldsWrapper>
              <Label htmlFor="city" variant="body1">
                {i18n.t('common.city')}
              </Label>
              <FormStyle.TextWrapper>
                <Field
                  name="city"
                  id="city"
                  handleOnChange={(event) => handleEdit(event, { field: 'city' })}
                  as={Input}
                  placeholder={i18n.t('common.city')}
                  value={values['city']}
                  type="text"
                />
                {showError('city', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
              </FormStyle.TextWrapper>
            </FormStyle.FieldsWrapper>

            <FormStyle.FieldsWrapper>
              <Label htmlFor="state" variant="body1">
                {i18n.t('common.state')}
              </Label>
              <FormStyle.TextWrapper>
                <Field
                  name="state"
                  id="state"
                  onChange={(event) => {
                    handleEdit(event, { field: 'state' });
                  }}
                  as={Select}
                  placeholderText={i18n.t('common.state')}
                  value={values['state']}
                  suggestions={states}
                />
                {showError('state', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
              </FormStyle.TextWrapper>
            </FormStyle.FieldsWrapper>
            {checkServiceProvider('leftform_fields_initialTrucks', window.$environment.SERVICE_PROVIDER, false) &&
              type !== 'edit' && (
                <FormStyle.FieldsWrapper>
                  <Label htmlFor="initialTrucks" variant="body1">
                    {i18n.t('common.noofIntialCamera')}
                  </Label>
                  <FormStyle.TextWrapper>
                    <Field
                      name="initialTrucks"
                      id="initialTrucks"
                      handleOnChange={(event) => handleEdit(event, { field: 'initialTrucks' })}
                      as={Input}
                      value={values['initialTrucks']}
                      type="number"
                    />
                    {showError('initialTrucks', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
                  </FormStyle.TextWrapper>
                </FormStyle.FieldsWrapper>
              )}
            <FormStyle.FieldsWrapper width="100%">
              <Label variant="body1">{i18n.t('companyData.expiryDate')}</Label>
              <FormStyle.TextWrapper className="userForm">
                <Field
                  name="insuranceExpDate"
                  onChange={(event) => {
                    handleEdit(event, {
                      field: 'insuranceExpDate',
                      type: 'date',
                    });
                    // setNewValues('hireDate', event)
                  }}
                  locale={getDateLocale()}
                  as={FormikDatePicker}
                  placeholderText={UI_DATE_LOWER_FORMAT}
                  value={values['insuranceExpDate']}
                  type="date"
                  showPopperArrow={false}
                  showTimeSelect={false}
                  showDisabledMonthNavigation
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  isClearable={true}
                />
                {showError('insuranceExpDate', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
              </FormStyle.TextWrapper>
            </FormStyle.FieldsWrapper>

            {checkServiceProvider('leftform_fields_speedLimit', window.$environment.SERVICE_PROVIDER, false) && (
              <FormStyle.FieldsWrapper>
                <Label htmlFor="speedLimit" variant="body1">
                  {i18n.t('companies.speedLimit')}
                </Label>
                <FormStyle.TextWrapper>
                  <Field
                    name="speedLimit"
                    id="speedLimit"
                    handleOnChange={(event) => handleEdit(event, { field: 'speedLimit' })}
                    as={Input}
                    placeholder={i18n.t('companies.speedLimit')}
                    value={values['speedLimit']}
                    type="number"
                  />
                  {showError('speedLimit', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
                </FormStyle.TextWrapper>
              </FormStyle.FieldsWrapper>
            )}
          </div>
          <div className="cycleruleSection">
            <SubHeader
              text={i18n.t('companyData.fleetManagerInformation')}
              className="subheaderSection companyTitle"
              variant="h2"
            />
            <FormStyle.FieldsWrapper>
              <Label htmlFor="firstName" variant="body1">
                {i18n.t('common.firstName')}
              </Label>
              <FormStyle.TextWrapper>
                <Field
                  name="firstName"
                  id="firstName"
                  handleOnChange={(event) => handleEdit(event, { field: 'firstName' })}
                  as={Input}
                  placeholder={i18n.t('common.firstName')}
                  value={values['firstName']}
                  type="text"
                />
                {showError('firstName', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
              </FormStyle.TextWrapper>
            </FormStyle.FieldsWrapper>
            <FormStyle.FieldsWrapper>
              <Label htmlFor="lastName" variant="body1">
                {i18n.t('common.lastName')}
              </Label>
              <FormStyle.TextWrapper>
                <Field
                  name="lastName"
                  id="lastName"
                  handleOnChange={(event) => handleEdit(event, { field: 'lastName' })}
                  as={Input}
                  placeholder={i18n.t('common.lastName')}
                  value={values['lastName']}
                  type="text"
                />
                {showError('lastName', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
              </FormStyle.TextWrapper>
            </FormStyle.FieldsWrapper>
            <FormStyle.FieldsWrapper>
              <Label htmlFor="email" variant="body1">
                {i18n.t('common.email')}
              </Label>
              <FormStyle.TextWrapper>
                <Field
                  name="email"
                  id="email"
                  handleOnChange={(event) => handleEdit(event, { field: 'email' })}
                  as={Input}
                  placeholder={i18n.t('common.email')}
                  value={values['email']}
                  type="text"
                />
                {showError('email', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
              </FormStyle.TextWrapper>
            </FormStyle.FieldsWrapper>
            <FormStyle.FieldsWrapper>
              <Label htmlFor="userPhone" variant="body1">
                {i18n.t('common.phone')}
              </Label>
              <FormStyle.TextWrapper>
                <Field
                  name="userPhone"
                  id="userPhone"
                  handleOnChange={(event) => handleEdit(event, { field: 'userPhone' })}
                  as={Input}
                  placeholder={i18n.t('common.phone')}
                  value={values['userPhone']}
                  updateValue={phoneNumberValidation}
                  type="text"
                />
                {showError('userPhone', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
              </FormStyle.TextWrapper>
            </FormStyle.FieldsWrapper>
            <FormStyle.FieldsWrapper>
              <Label htmlFor="password" variant="body1">
                {i18n.t('common.password')}
              </Label>
              <FormStyle.TextWrapper>
                <Field
                  name="password"
                  id="password"
                  handleOnChange={(event) => handleEdit(event, { field: 'password' })}
                  as={Input}
                  placeholder={i18n.t('common.password')}
                  value={values['password']}
                  type="text"
                />
                {showError('password', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
              </FormStyle.TextWrapper>
            </FormStyle.FieldsWrapper>
          </div>
        </>
      )}
    </Styled.leftSection>
  );
}
